import React, { useEffect, useState } from 'react'
import PageConfig from '@/component/layout/PageConfig.tsx'
import SearchBar from '@/component/input/SearchBar.tsx'
import SearchUserItem from '@/pages/users/SearchUserItem.tsx'
import useQueryGetUsers from '@/api/user/getUsers.ts'
import useDebounce from '@/util/hook/useDebounce.tsx'
import UserDetail from '@/pages/users/UserDetail.tsx'
import { useSearchParams } from 'react-router-dom'
import { isEmptyArray, isNotEmptyArray } from '@/util/array.ts'
import Pagination from '@/component/pagination/Pagination.tsx'
import { useTranslation } from 'react-i18next'

type IProps = {}

const PAGE_SIZE = 10

const Users: React.FC<IProps> = ({}) => {
    const { t } = useTranslation()

    const [searchParams, setSearchParams] = useSearchParams()

    const [search, setSearch] = useState('')
    const [userId, setUserId] = useState(Number(searchParams.get('id')))
    const [page, setPage] = useState(1) // 페이지는 1부터 시작

    const debouncedSearchText = useDebounce(search, 500) // 검색어 디바운스 시간 증가

    const { data, refetch, isLoading } = useQueryGetUsers(
        {
            pageNum: page,
            pageSize: PAGE_SIZE
        },
        debouncedSearchText
    )

    const users = data?.data || []
    const pagination = data?.pagination

    // 첫 번째 사용자를 자동 선택 (userId가 설정되지 않았을 때)
    useEffect(() => {
        if (userId <= 0 && users?.length > 0) {
            setUserId(users[0].id)
        }
    }, [data, userId])

    // 검색어가 변경되면 첫 페이지로 이동
    useEffect(() => {
        setPage(1)
    }, [debouncedSearchText])

    const onRefresh = () => {
        setUserId(-1)
        refetch()
    }

    const handlePageChange = (newPage: number) => {
        setPage(newPage)
    }

    useEffect(() => {
        setSearchParams({ id: userId?.toString() })
    }, [userId])

    return (
        <>
            <PageConfig showTopNav={true} showSideNav={true}>
                {!isLoading && (
                    <div className={'grid grid-cols-[340px_auto] h-full'}>
                        <div className={'bg-white p-[20px] overflow-y-auto'}>
                            <div className={'flex gap-[7px]'}>
                                <SearchBar
                                    inputParams={{
                                        placeholder: t('users.list.search.placeholder')
                                    }}
                                    onChanged={(text) => setSearch(text)}
                                />
                            </div>
                            {isEmptyArray(users) && (
                                <div className={'text-body text-gray4 text-center mt-[80px]'}>
                                    {debouncedSearchText
                                        ? t('users.list.empty.search')
                                        : t('users.list.empty')}
                                </div>
                            )}
                            {isNotEmptyArray(users) && (
                                <div>
                                    <div className={'mt-[20px] grid gap-[10px] overflow-y-auto'}>
                                        {users?.map((item) => (
                                            <SearchUserItem
                                                {...item}
                                                key={item?.id}
                                                onClick={(id: number) => setUserId(id)}
                                            />
                                        ))}
                                    </div>
                                    {pagination && pagination.totalPages > 1 && (
                                        <Pagination
                                            className={'mt-[40px]'}
                                            page_size={pagination.pageSize}
                                            current_page={pagination.currentPage}
                                            total_page={pagination.totalPages}
                                            fetchPage={handlePageChange}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                        {userId > 0 && (
                            <UserDetail
                                className={'overflow-y-auto'}
                                id={userId}
                                onRefresh={onRefresh}
                            />
                        )}
                        {userId <= 0 && (
                            <div
                                className={
                                    'bg-sub_b h-full flex flex-col justify-center items-center'
                                }>
                                <p className={'mt-[13px] text-body'}>{t('users.list.empty')}</p>
                            </div>
                        )}
                    </div>
                )}
            </PageConfig>
        </>
    )
}

export default Users
